/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { logInfoToProvider, emitLogToProvider } from "logging/logToServer";
import {
  isFilePasswordProtected,
  getIsFilteredDocOpenActionsPresent,
  getDocumentType,
  isDocumentEncrypted,
  getAssetRegion,
  getPdfnowVerb,
  getExtensionDetected
} from "selectors/documentSelectors";
import { calculateNetworkSpeed } from "utils/performance";
import OrganizePDFFileFormats from "organize/utils/OrganizePDFFileFormats";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import * as markers from "constants/perf-markers";
import { getFeatureFlagApis } from "../publicApis/featureFlag";
import { getCaretMode } from "selectors/caretModeSelectors";
import { getIsCursorVisible } from "dc-preview-utils/src/selectors/cursorSelectors";
import { isDropinLoaded } from "selectors/overlayDropinSelectors";

let shouldLogKeyPressBeforeDropinLoad = false;

export const addNetworkLoggingToPerfObject = () => {
  const networkObj = {};
  if (window.performance && typeof window.performance.getEntriesByType === "function") {
    let total = 0,
      cacheHits = 0;
    const perfRegexListCopy = markers.perfRegexList.map(regexEntry => ({ ...regexEntry, found: false }));
    window.performance.getEntriesByType("resource").forEach(item => {
      if (["script", "link", "css", "early-hints"].includes(item.initiatorType)) {
        total += 1;
        if (item.deliveryType === "cache") {
          cacheHits += 1;
        }
      }
      perfRegexListCopy.forEach(regexEntry => {
        if (!regexEntry.found && regexEntry.regex.test(item.name)) {
          regexEntry.found = true;
          if (regexEntry.shouldLogSpeed) {
            networkObj[`${regexEntry.name}-speed-Mbps`] = calculateNetworkSpeed(item);
          }
          if (regexEntry.name === "wasmPrefetch") {
            networkObj.wasmCache = Number(item.deliveryType === "cache");
            networkObj.wasmInitiatorType = item.initiatorType;
          }
          if (regexEntry.name === "wasmJsPrefetch") {
            networkObj.wasmJsCache = Number(item.deliveryType === "cache");
          }
          if (regexEntry.shouldLogDurationInAcrobatPerfEvent) {
            networkObj[`${regexEntry.name}-duration`] = Math.round(item.duration);
          }
        }
      });
    });
    if (total > 0) {
      networkObj.cacheHitRatio = cacheHits / total;
    }
  }
  return networkObj;
};

export const logFirstPageLoaded = (data, stores) => {
  const isPasswordProtected = isFilePasswordProtected(stores);
  const isFilteredDocOpenActionsPresent = getIsFilteredDocOpenActionsPresent(stores);
  const documentType = getDocumentType(stores);
  const isEncrypted = isDocumentEncrypted(stores);
  const assetRegion = getAssetRegion(stores);
  const pdfnowVerb = getPdfnowVerb(stores);
  const extensionDetected = getExtensionDetected(stores);
  const automationObj = {};
  if (getQueryParameterFromUrl(window.location.href, "isPerfAutomation") === "true") {
    automationObj.automationSessionId = getQueryParameterFromUrl(window.location.href, "sessionId");
    automationObj.automationBuild = getQueryParameterFromUrl(window.location.href, "build");
    automationObj.automationEnv = getQueryParameterFromUrl(window.location.href, "env");
  }

  const networkObj = addNetworkLoggingToPerfObject();

  logInfoToProvider("acrobatPerformanceMarkers", {
    ...data,
    ...networkObj,
    loggerEmit: true,
    isPasswordProtected,
    isFilteredDocOpenActionsPresent,
    documentType,
    isEncrypted,
    assetRegion,
    pdfnowVerb,
    extensionDetected,
    ...automationObj
  });
};

export const logFirstRasterRequested = time => {
  logInfoToProvider("%O", { FirstRasterRequested: time });
};

export const logFirstRasterRendition = time => {
  logInfoToProvider("%O", { LoadTimeFirstRasterRendition: time });
};

export const logAJSRenderingStart = time => {
  logInfoToProvider("%O", { AJSRenderingStartedTime: time });
};

export const logSecondaryMetadataFailed = (msg, isExternal = false) => {
  if (isExternal) {
    logInfoToProvider("%O", {
      ExternalMetadataFailed: {
        reason: msg
      }
    });
    return;
  }
  logInfoToProvider("%O", {
    DCAPIMetadataFailed: {
      reason: msg
    }
  });
};

export const logFirstRenditionTypeVisible = (time, type) => {
  logInfoToProvider("%O", {
    FirstRenditionTypeVisible: {
      time,
      type
    }
  });
};

export const logPageRenderTime = renderingStats => {
  logInfoToProvider("%O", renderingStats);
};

export const logAcroformLoadTime = acroformLoadTime => {
  const infoObj = { jSAnalytics: { acroformLoadTime } };
  logInfoToProvider("%O", infoObj);
};

export const logAJSInfo = ajsInfo => {
  logInfoToProvider("%O", { AJSInfo: ajsInfo });
};

export const logPreviewInitTime = time => {
  logInfoToProvider("%O", { PreviewInitTime: time });
};

export const logJSActionsData = data => {
  logInfoToProvider("%O", { JSActionData: data });
};

export const logNamedDestinationData = data => {
  logInfoToProvider("%O", { namedDestsData: data });
};

export const logNonJSActionTriggerPoints = data => {
  logInfoToProvider("%O", { NonJSActionData: data });
};

export const logUnsupportedWidgetShown = data => {
  logInfoToProvider("%O", {
    AcroformUnsupportedWidget: {
      unsupportedWidgetShown: true,
      ...data
    }
  });
};

export const logUnsupportedFileDialogShown = data => {
  logInfoToProvider("%O", {
    unSupportedDialogShown: true,
    feature: data.feature,
    opType: data.opType,
    reason: data.failureType
  });
};

export const logSharedFileDialogShown = data => {
  logInfoToProvider("%O", { sharedFileDialogShown: true, ...data });
};

export const logOrganizeErrorData = data => {
  logInfoToProvider("%O", data);
};

const _getKeyByMimeType = mimeType => {
  if (mimeType === "application/pdf") {
    return "pdf";
  }
  for (const format of OrganizePDFFileFormats.file_formats) {
    for (const key in format) {
      if (format[key].mime_type === mimeType) {
        return key;
      }
    }
  }
  return null;
};

export const logOrganizeInsertData = ({ mimeType, ...rest }) => {
  logInfoToProvider("%O", { mimeType: _getKeyByMimeType(mimeType), ...rest });
};

export const logOrganizeCommonData = data => {
  logInfoToProvider("%O", { ...data });
};

export const logUnsupportedWidgetClosed = () => {
  logInfoToProvider("%O", { AcroformUnsupportedWidgetClosed: true });
};

export const logUnsupportedWidgetOpenInDesktopClicked = () => {
  logInfoToProvider("%O", { AcroformUnsupportedWidgetOpenInDesktopClicked: true });
};

export const logGetPageRenditionCallInfo = data => {
  logInfoToProvider("%O", { pageRenditionLogData: data });
};

export const logMaxZoomReached = () => {
  logInfoToProvider("%O", { maxZoomReached: true });
};

export const logCacheBurstInfo = data => {
  logInfoToProvider("%O", { cacheBurstInfo: data });
};

export const logResourceMarkers = stores => {
  const obj = {};
  if (window.performance && typeof window.performance.getEntriesByType === "function") {
    if (window.performance?.getEntriesByName(markers.VIEWER_ALM_CONFIGURATOR_INIT)) {
      const almMarker = window.performance?.getEntriesByName(markers.VIEWER_ALM_CONFIGURATOR_INIT);
      if (almMarker.length > 0) {
        obj[markers.VIEWER_ALM_CONFIGURATOR_INIT] = Math.round(almMarker[0].startTime);
      }
    }
    const perfRegexListCopy = markers.perfRegexList.map(regexEntry => ({ ...regexEntry, found: false }));
    window.performance.getEntriesByType("resource").forEach(item => {
      perfRegexListCopy.forEach(regexEntry => {
        if (!regexEntry.found && regexEntry.regex.test(item.name)) {
          regexEntry.found = true;
          obj[`${regexEntry.name}-duration`] = Math.round(item.duration);
          obj[`${regexEntry.name}-start-time`] = Math.round(item.startTime);

          if (regexEntry.shouldLogBreakdown) {
            obj[`${regexEntry.name}-request-start-time`] = Math.round(item.requestStart);
            obj[`${regexEntry.name}-response-start-time`] = Math.round(item.responseStart);
          }

          if (regexEntry.shouldLogSpeed) {
            obj[`${regexEntry.name}-speed-Mbps`] = calculateNetworkSpeed(item);
          }
        }
      });
    });
  }
  const assetRegion = getAssetRegion(stores);
  const isPasswordProtected = isFilePasswordProtected(stores);
  const automationObj = {};
  if (getQueryParameterFromUrl(window.location.href, "isPerfAutomation") === "true") {
    automationObj.automationSessionId = getQueryParameterFromUrl(window.location.href, "sessionId");
    automationObj.automationBuild = getQueryParameterFromUrl(window.location.href, "build");
    automationObj.automationEnv = getQueryParameterFromUrl(window.location.href, "env");
  }
  logInfoToProvider("%O", { networkLogging: obj, assetRegion, isPasswordProtected, ...automationObj });
};

export const logCacheHitAndMissInfo = data => {
  logInfoToProvider("%O", { cacheHitAndMissInfo: data });
};

export const logMsgsAndWaitTimers = data => {
  logInfoToProvider("%O", { cmdProcessingTimes: data });
};

export const logDocumentAge = data => {
  logInfoToProvider("%O", data);
};

export const logCNPDFComponentLogs = (message, data) => {
  emitLogToProvider(message, { ...data, isCNPDF: true });
};

export const resetKeyPressLogging = () => {
  shouldLogKeyPressBeforeDropinLoad = true;
};

export const logKeyDownForEditShortcutCaretModeFeature = stores => {
  if (shouldLogKeyPressBeforeDropinLoad) {
    // We want to log user key Press when Caret mode feature is enabled
    // To track that how many user try to type something after caret is visible but no action is performed
    // we will log when cursor is visible and edit dropin is not loaded yet
    if (
      getCaretMode(stores) &&
      getIsCursorVisible(stores) &&
      !(isDropinLoaded("partial-edit", stores) || isDropinLoaded("edit", stores))
    ) {
      shouldLogKeyPressBeforeDropinLoad = false;
      getFeatureFlagApis()
        .getFeatureFlag("DCWeb_edit_keyboard_shortcut_caret_mode")
        .then(isFeatureEnabled => {
          if (isFeatureEnabled) {
            logInfoToProvider("%O", { keyPressBeforeDropinLoadWhenCaretVisible: true });
          }
        });
    }
  }
};
